<template>
  <div class="flex h-screen bg-gray-950 text-white">
    <!-- Sidebar -->
    <aside
      class="w-64 bg-gradient-to-b from-gray-900 to-gray-950 p-6 flex flex-col space-y-6 border-r border-gray-800 shadow-xl"
    >
      <div class="flex items-center justify-center space-x-3">
        <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-16" />
      </div>
      <Navbar />
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col">
      <!-- Top Navbar -->
      <Header />

      <main class="p-8 flex-1 flex gap-8 bg-gray-950">
        <!-- Storage Purchase Section -->
        <div
          class="flex-1 p-6 bg-gradient-to-b from-gray-900 to-gray-950 border border-gray-800 rounded-2xl shadow-lg"
        >
          <h2 class="text-xl font-bold mb-6">Purchase Cloud Storage</h2>

          <!-- Storage Type Selection -->
          <div class="mb-6">
            <h3 class="text-sm uppercase text-gray-400 mb-2">Storage Type</h3>
            <div class="flex gap-4">
              <button
                v-for="type in storageTypes"
                :key="type"
                @click="selectStorageType(type)"
                class="px-4 py-2 rounded-lg border border-gray-700 transition"
                :class="selectedStorageType === type ? 'bg-blue-600 border-blue-500 shadow-md' : 'bg-gray-800 hover:bg-gray-700'"
              >
                {{ type }}
              </button>
            </div>
          </div>

          <!-- Storage Size Selection -->
          <div class="mb-6">
            <h3 class="text-sm uppercase text-gray-400 mb-2">Select Storage Size</h3>
            <div class="overflow-hidden border border-gray-700 rounded-xl">
              <table class="w-full text-left divide-y divide-gray-700">
                <thead class="bg-gray-800/70">
                  <tr>
                    <th class="p-3">Size</th>
                    <th class="p-3">Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="size in selectedStorageSizes"
                    :key="size.name"
                    class="cursor-pointer hover:bg-gray-800 transition"
                    @click="selectStorageSize(size)"
                    :class="selectedStorageSize === size.name ? 'bg-blue-600/20' : ''"
                  >
                    <td class="p-3">{{ size.name }}</td>
                    <td class="p-3">{{ size.price }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Storage Options -->
          <div>
            <h3 class="text-sm uppercase text-gray-400 mb-2">Storage Options</h3>
            <div class="grid grid-cols-3 gap-4">
              <div
                v-for="option in storageOptions"
                :key="option"
                @click="toggleOption(option)"
                class="bg-gray-800 p-4 rounded-xl text-center cursor-pointer border transition"
                :class="selectedOptions.includes(option) ? 'border-blue-500 bg-blue-600/20 shadow-md' : 'border-gray-700 hover:border-gray-500'"
              >
                {{ option }}
              </div>
            </div>
          </div>
        </div>

        <!-- Order Summary Section -->
        <div
          class="w-1/3 p-6 h-fit sticky top-8 bg-gradient-to-b from-gray-900 to-gray-950 border border-gray-800 rounded-2xl shadow-lg"
        >
          <h2 class="text-xl font-bold mb-6">Order Summary</h2>

          <div class="bg-gray-800 rounded-xl p-4 mb-6 space-y-2">
            <p><span class="text-gray-400">Type:</span> {{ selectedStorageType || 'N/A' }}</p>
            <p><span class="text-gray-400">Size:</span> {{ selectedStorageSize || 'N/A' }}</p>
            <p><span class="text-gray-400">Price:</span> {{ selectedStoragePrice || 'N/A' }}</p>
          </div>

          <h3 class="text-sm uppercase text-gray-400 mb-2">Selected Options</h3>
          <ul class="bg-gray-800 p-4 rounded-xl space-y-1">
            <li v-for="option in selectedOptions" :key="option">- {{ option }}</li>
            <li v-if="selectedOptions.length === 0" class="text-gray-500">No options selected</li>
          </ul>

          <button
            class="w-full mt-6 px-6 py-3 bg-blue-600 rounded-xl hover:bg-blue-700 transition font-semibold shadow-lg"
          >
            Proceed to Checkout
          </button>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/nav.vue';
import Header from '@/components/header.vue';

export default {
  name: 'StoragePage',
  data() {
    return {
      storageTypes: ['Standard', 'High Performance', 'Cold Storage'],
      selectedStorageType: 'Standard',
      storageSizes: {
        Standard: [
          { name: '100GB', price: '$5/month' },
          { name: '500GB', price: '$20/month' }
        ],
        'High Performance': [
          { name: '100GB', price: '$10/month' },
          { name: '500GB', price: '$40/month' }
        ],
        'Cold Storage': [
          { name: '1TB', price: '$2/month' },
          { name: '10TB', price: '$15/month' }
        ]
      },
      selectedStorageSizes: [],
      selectedStorageSize: '',
      selectedStoragePrice: '',
      storageOptions: ['Encryption', 'Automated Backups', 'Geo-Replication'],
      selectedOptions: []
    };
  },
  components: { Navbar, Header },
  methods: {
    selectStorageType(type) {
      this.selectedStorageType = type;
      this.selectedStorageSizes = this.storageSizes[type] || [];
      this.selectedStorageSize = '';
      this.selectedStoragePrice = '';
    },
    selectStorageSize(size) {
      this.selectedStorageSize = size.name;
      this.selectedStoragePrice = size.price;
    },
    toggleOption(option) {
      if (this.selectedOptions.includes(option)) {
        this.selectedOptions = this.selectedOptions.filter((opt) => opt !== option);
      } else {
        this.selectedOptions.push(option);
      }
    }
  },
  mounted() {
    this.selectStorageType(this.selectedStorageType);
  }
};
</script>
